import React from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import drawTimeline from '../utils/drawTimeline.js';
import {
  buildImportTimeline,
  clampPosition,
  visibleSegments,
  WINDOW_SECONDS,
} from '../utils/importTimeline.js';

export default function WaveformViewer({ imports, recordings }) {
  const [selectedId, setSelectedId] = React.useState('');
  const [start, setStart] = React.useState(0);
  const [gain, setGain] = React.useState(1);
  const canvas = React.useRef(null);
  const container = React.useRef(null);
  const drag = React.useRef(null);
  const [width, setWidth] = React.useState(900);
  const activeImport =
    imports.find((entry) => entry.id === selectedId) ||
    imports.find((entry) => entry.success > 0) ||
    imports[0];
  const activeId = activeImport?.id || '';
  React.useEffect(() => {
    if (activeId !== selectedId) {
      setSelectedId(activeId);
      setStart(0);
    }
  }, [activeId, selectedId]);
  const timeline = React.useMemo(
    () => buildImportTimeline(recordings, activeId),
    [recordings, activeId]
  );
  const hasRecording = timeline.segments.length > 0;
  const maxStart = Math.max(0, timeline.total - WINDOW_SECONDS);
  const position = clampPosition(start, timeline.total);
  const visible = visibleSegments(timeline, position);
  const first = visible[0];
  const move = React.useCallback(
    (seconds) => {
      setStart((current) => clampPosition(current + seconds, timeline.total));
    },
    [timeline.total]
  );
  React.useEffect(() => {
    if (!container.current) return undefined;
    const observer = new ResizeObserver(([entry]) =>
      setWidth(Math.max(280, Math.floor(entry.contentRect.width)))
    );
    observer.observe(container.current);
    return () => observer.disconnect();
  }, [hasRecording]);
  React.useEffect(() => {
    const element = canvas.current;
    if (!element) return undefined;
    const handleWheel = (event) => {
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY) || event.shiftKey) {
        event.preventDefault();
        const scale = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? width : 1;
        move((event.deltaX || event.deltaY) * scale * 0.01);
      }
    };
    element.addEventListener('wheel', handleWheel, { passive: false });
    return () => element.removeEventListener('wheel', handleWheel);
  }, [move, hasRecording, width]);
  React.useEffect(() => {
    if (!hasRecording || !canvas.current) return;
    const height = timeline.leads.length * 180 + 65;
    const ratio = window.devicePixelRatio || 1;
    const element = canvas.current;
    element.width = width * ratio;
    element.height = height * ratio;
    element.style.height = `${height}px`;
    const ctx = element.getContext('2d');
    ctx.scale(ratio, ratio);
    drawTimeline(ctx, timeline, position, width, gain);
  }, [timeline, position, gain, width, hasRecording]);
  const handleImportChange = (event) => {
    setSelectedId(event.target.value);
    setStart(0);
    drag.current = null;
  };
  const handleGainChange = (event) => setGain(Number(event.target.value));
  const handleSliderChange = (event, value) => setStart(value);
  const handleKeyDown = (event) => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      move((event.key === 'ArrowLeft' ? -1 : 1) * (event.shiftKey ? 10 : 1));
    }
  };
  const handlePointerDown = (event) => {
    if (event.button !== 0) return;
    event.currentTarget.focus();
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { x: event.clientX, start: position };
  };
  const handlePointerMove = (event) => {
    if (drag.current)
      setStart(
        clampPosition(
          drag.current.start -
            ((event.clientX - drag.current.x) * WINDOW_SECONDS) / Math.max(1, width - 72),
          timeline.total
        )
      );
  };
  const handlePointerEnd = () => {
    drag.current = null;
  };
  return (
    <Box>
      <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap', mb: 2 }}>
        <TextField
          select
          size="small"
          label="Import"
          value={activeId}
          onChange={handleImportChange}
          disabled={!imports.length}
          sx={{ minWidth: 280, maxWidth: '100%' }}
        >
          {!imports.length && <MenuItem value="">No imports</MenuItem>}
          {imports.map((entry) => (
            <MenuItem key={entry.id} value={entry.id}>
              Import #{entry.number} — {entry.date} — {entry.success} successful
            </MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="Amplitude" value={gain} onChange={handleGainChange}>
          {[0.25, 0.5, 1, 2, 4].map((value) => (
            <MenuItem key={value} value={value}>
              {value}×
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      {!hasRecording ? (
        <Typography>No successfully parsed recordings in this import yet.</Typography>
      ) : (
        <>
          <Typography variant="body2">
            {timeline.segments.length} files · {timeline.total.toFixed(2)} seconds of ECG · Viewing{' '}
            {position.toFixed(2)}–{Math.min(position + WINDOW_SECONDS, timeline.total).toFixed(2)}{' '}
            seconds
          </Typography>
          <Typography variant="body2">
            At left: {first?.recording.recordedAt || 'Unknown recording time'} +{' '}
            {Math.max(0, position - (first?.start || 0)).toFixed(2)}s · {first?.recording.path} ·{' '}
            {first?.recording.sampleRate} Hz
          </Typography>
          <Typography variant="body2">
            Each major vertical amplitude interval = {(1 / gain).toFixed(2)} mV. Files scroll
            back-to-back; dashed boundaries identify recording gaps or overlaps. Device dates are
            not timezone-converted.
          </Typography>
          {activeImport?.failed > 0 && (
            <Typography color="error" variant="body2">
              {activeImport.failed} failed files are excluded. See import failure details above.
            </Typography>
          )}
          <Box ref={container} sx={{ mt: 2, width: '100%' }}>
            <canvas
              ref={canvas}
              tabIndex={0}
              aria-label="Continuous ECG waveform for selected import. Left and right arrows pan across files. Shift plus arrows moves ten seconds."
              style={{
                display: 'block',
                width: '100%',
                touchAction: 'pan-y',
                cursor: 'grab',
                border: '1px solid #ddd',
              }}
              onKeyDown={handleKeyDown}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerEnd}
              onPointerCancel={handlePointerEnd}
              onLostPointerCapture={handlePointerEnd}
            />
          </Box>
          <Slider
            value={position}
            min={0}
            max={maxStart || 1}
            step={0.004}
            disabled={!maxStart}
            onChange={handleSliderChange}
            aria-label="Import timeline position"
          />
          <Stack direction="row" spacing={2}>
            <Button onClick={() => move(-1)} disabled={position === 0}>
              ← Back
            </Button>
            <Button onClick={() => move(1)} disabled={position === maxStart}>
              Forward →
            </Button>
          </Stack>
          <Typography variant="body2">
            Drag or use the slider to scan the whole import. Click the waveform and use ← / →;
            Shift+arrow moves 10 seconds. Horizontal scrolling or Shift+wheel also pans across
            files.
          </Typography>
        </>
      )}
    </Box>
  );
}
WaveformViewer.propTypes = {
  imports: PropTypes.array.isRequired,
  recordings: PropTypes.array.isRequired,
};
